import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { IndianRupee, Plus, CheckCircle2, XCircle } from "lucide-react";

const CATEGORIES = ["salary", "rent", "utilities", "supplies", "maintenance", "transport", "marketing", "events", "other"];
const INITIAL = { title: "", category: "supplies", amount: "", payment_mode: "cash", notes: "" };

export default function Expenses() {
  const { user } = useAuth();
  const isFounder = user?.role === "founder";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/expenses");
      setItems(data);
      setError("");
    } catch {
      setError("Unable to load expenses. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (event) => {
    event.preventDefault();
    if (saving || !/^\d+(\.\d{1,2})?$/.test(form.amount) || Number(form.amount) <= 0) {
      if (!saving) toast.error("Enter an amount with up to two decimal places");
      return;
    }
    setSaving(true);
    try {
      await api.post("/finance/expenses", form);
      toast.success("Expense submitted for Founder approval");
      setForm(INITIAL);
      await refresh();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Expense submission failed");
    } finally {
      setSaving(false);
    }
  };

  const markPaid = async (id) => {
    if (busyId) return;
    const payment_mode = window.prompt("Payment mode: cash, bank, upi or other", "bank");
    if (payment_mode === null) return;
    if (!["cash", "bank", "upi", "other"].includes(payment_mode.trim().toLowerCase())) { toast.error("Invalid payment mode"); return; }
    const reference = window.prompt("Transaction reference (optional):", "");
    if (reference === null) return;
    setBusyId(id);
    try {
      await api.post(`/finance/expenses/${id}/mark-paid`, { payment_mode: payment_mode.trim().toLowerCase(), reference: reference.trim() });
      toast.success("Payment recorded");
      await refresh();
    } catch { toast.error("Payment recording failed"); }
    finally { setBusyId(null); }
  };

  const decide = async (id, decision) => {
    if (busyId) return;
    let remarks = "";
    if (decision === "rejected") {
      const input = window.prompt("Reason for rejection:");
      if (input === null) return;
      remarks = input.trim();
      if (!remarks) { toast.error("A rejection reason is required"); return; }
    }
    setBusyId(id);
    try {
      await api.post(`/finance/expenses/${id}/decision`, { decision, remarks });
      toast.success(`Expense ${decision}`);
      await refresh();
    } catch {
      toast.error("Action failed. This request may already have been reviewed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-black">Expense Management</h1>
        <p className="text-muted-foreground">
          {isFounder ? "Review school expense requests" : "Submit expenses for Founder approval"}
        </p>
      </div>

      {!isFounder && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Plus size={18} /> New Expense</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label htmlFor="expense-title">Title</Label>
                <Input id="expense-title" required minLength={3} maxLength={200} value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div><Label htmlFor="expense-amount">Amount (INR)</Label>
                <Input id="expense-amount" required inputMode="decimal" type="text" placeholder="125.50" value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
              </div>
              <div><Label htmlFor="expense-category">Category</Label>
                <select id="expense-category" className="w-full h-10 rounded-md border bg-background px-3" value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                </select>
              </div>
              <div><Label htmlFor="expense-mode">Proposed payment method</Label>
                <select id="expense-mode" className="w-full h-10 rounded-md border bg-background px-3" value={form.payment_mode}
                  onChange={e => setForm(f => ({ ...f, payment_mode: e.target.value }))}>
                  {["cash", "bank", "upi", "other"].map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                </select>
              </div>
              <div className="md:col-span-2"><Label htmlFor="expense-notes">Notes</Label>
                <Input id="expense-notes" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <Button type="submit" disabled={saving}>{saving ? "Submitting..." : "Submit Expense"}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><IndianRupee size={20} /> Expense Requests</CardTitle></CardHeader>
        <CardContent>
          {error && <p className="text-red-600 mb-3" role="alert">{error} <Button variant="outline" size="sm" onClick={refresh}>Retry</Button></p>}
          {loading ? <p>Loading expenses...</p> : items.length === 0 ? <p className="text-muted-foreground">No expense requests found.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead><tr className="border-b">
                  <th className="p-3">Expense</th><th className="p-3">Category</th>
                  <th className="p-3">Amount</th><th className="p-3">Approval</th>
                  <th className="p-3">Payment</th><th className="p-3">Actions</th>
                </tr></thead>
                <tbody>{items.map(x => <tr key={x.id} className="border-b">
                  <td className="p-3 font-medium">{x.title}</td>
                  <td className="p-3 capitalize">{x.category}</td>
                  <td className="p-3">₹{(x.amount_paise / 100).toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                  <td className="p-3 capitalize">{x.status}</td>
                  <td className="p-3 capitalize">{x.payment_status || "unpaid"}</td>
                  <td className="p-3">
                    {isFounder && x.status === "pending" && <div className="flex gap-2">
                      <Button size="sm" disabled={!!busyId} onClick={() => decide(x.id, "approved")}><CheckCircle2 size={15} className="mr-1" /> Approve</Button>
                      <Button size="sm" variant="outline" disabled={!!busyId} onClick={() => decide(x.id, "rejected")}><XCircle size={15} className="mr-1" /> Reject</Button>
                    </div>}
                    {!isFounder && x.status === "approved" && x.payment_status !== "paid" && <Button size="sm" variant="outline" disabled={!!busyId} onClick={() => markPaid(x.id)}>Record Payment</Button>}
                  </td>
                </tr>)}</tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
